import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Clock, Euro, Scissors, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { getServicios, createServicio, updateServicio, deleteServicio } from "@/lib/api";

export default function AdminServicios() {
  const [servicios, setServicios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingServicio, setEditingServicio] = useState(null);
  
  // Form state
  const [formNombre, setFormNombre] = useState("");
  const [formDuracion, setFormDuracion] = useState(30);
  const [formPrecio, setFormPrecio] = useState(15);
  const [formActivo, setFormActivo] = useState(true);
  const [formDescripcion, setFormDescripcion] = useState("");

  useEffect(() => {
    loadServicios();
  }, []);

  const loadServicios = async () => {
    try {
      const res = await getServicios();
      setServicios(res.data);
    } catch (error) {
      toast.error("Error al cargar servicios");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (servicio = null) => {
    if (servicio) {
      setEditingServicio(servicio);
      setFormNombre(servicio.nombre);
      setFormDuracion(servicio.duracion_minutos);
      setFormPrecio(servicio.precio);
      setFormActivo(servicio.activo);
      setFormDescripcion(servicio.descripcion || "");
    } else {
      setEditingServicio(null);
      setFormNombre("");
      setFormDuracion(30);
      setFormPrecio(15);
      setFormActivo(true);
      setFormDescripcion("");
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formNombre.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    if (formDuracion < 5) {
      toast.error("La duración mínima es 5 minutos");
      return;
    }
    if (formPrecio < 0) {
      toast.error("El precio no puede ser negativo");
      return;
    }

    const formData = {
      nombre: formNombre,
      duracion_minutos: formDuracion,
      precio: formPrecio,
      activo: formActivo,
      descripcion: formDescripcion
    };

    try {
      if (editingServicio) {
        await updateServicio(editingServicio.id, formData);
        toast.success("Servicio actualizado");
      } else {
        await createServicio(formData);
        toast.success("Servicio creado");
      }
      setShowModal(false);
      loadServicios();
    } catch (error) {
      toast.error("Error al guardar servicio");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar este servicio?")) return;
    
    try {
      await deleteServicio(id);
      toast.success("Servicio eliminado");
      loadServicios();
    } catch (error) {
      toast.error("Error al eliminar servicio");
    }
  };

  const handleToggleActivo = async (servicio) => {
    try {
      await updateServicio(servicio.id, {
        ...servicio,
        activo: !servicio.activo
      });
      toast.success(servicio.activo ? "Servicio desactivado" : "Servicio activado");
      loadServicios();
    } catch (error) {
      toast.error("Error al actualizar servicio");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner w-10 h-10" />
      </div>
    );
  }

  return (
    <div data-testid="admin-servicios">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading">Servicios</h1>
          <p className="text-muted-foreground mt-1">Configura los servicios que ofreces y sus precios</p>
        </div>
        <Button className="h-12 gap-2" onClick={() => handleOpenModal()} data-testid="btn-add-servicio">
          <Plus className="h-4 w-4" />
          Nuevo servicio
        </Button>
      </div>

      {servicios.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Scissors className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-heading mb-2">No hay servicios</h3>
            <p className="text-muted-foreground mb-4">Crea los servicios que ofrece tu peluquería</p>
            <Button onClick={() => handleOpenModal()}>
              <Plus className="h-4 w-4 mr-2" />
              Crear primer servicio
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {servicios.map(servicio => (
            <Card key={servicio.id} className={!servicio.activo ? "opacity-60 bg-muted/30" : ""}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="hidden md:flex text-muted-foreground cursor-grab">
                    <GripVertical className="h-5 w-5" />
                  </div>
                  
                  <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Scissors className="h-6 w-6 text-accent" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-medium truncate" data-testid={`servicio-nombre-${servicio.id}`}>
                        {servicio.nombre}
                      </h3>
                      {!servicio.activo && (
                        <Badge variant="secondary">Inactivo</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {servicio.duracion_minutos} min
                      </span>
                      {servicio.descripcion && (
                        <span className="hidden md:inline truncate max-w-xs">
                          {servicio.descripcion}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="text-right flex-shrink-0">
                    <div className="text-2xl font-heading text-accent">
                      {servicio.precio}€
                    </div>
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Switch
                      checked={servicio.activo}
                      onCheckedChange={() => handleToggleActivo(servicio)}
                      className="mr-2"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenModal(servicio)}
                      data-testid={`btn-edit-servicio-${servicio.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDelete(servicio.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Info card */}
      <Card className="mt-6 border-accent/50 bg-accent/5">
        <CardContent className="p-4">
          <h4 className="font-medium mb-2">💡 Consejos para tus servicios</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• <strong>Duración:</strong> Incluye tiempo extra para preparación entre clientes</li>
            <li>• <strong>Precio:</strong> Puedes modificarlo en cualquier momento</li>
            <li>• <strong>Inactivos:</strong> Los servicios inactivos no aparecen en la web de reservas</li>
          </ul>
        </CardContent>
      </Card>

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingServicio ? `Editar: ${editingServicio.nombre}` : "Nuevo servicio"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre del servicio *</Label>
              <Input
                placeholder="Ej: Corte hombre"
                value={formNombre}
                onChange={e => setFormNombre(e.target.value)}
                className="h-12"
                data-testid="modal-input-nombre"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Duración (minutos) *</Label>
                <Input
                  type="number"
                  min="5"
                  step="5"
                  value={formDuracion}
                  onChange={e => setFormDuracion(parseInt(e.target.value) || 0)}
                  className="h-12"
                  data-testid="modal-input-duracion"
                />
              </div>
              <div>
                <Label>Precio (€) *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={formPrecio}
                  onChange={e => setFormPrecio(parseFloat(e.target.value) || 0)}
                  className="h-12"
                  data-testid="modal-input-precio"
                />
              </div>
            </div>

            <div>
              <Label>Descripción (opcional)</Label>
              <Textarea
                placeholder="Ej: Incluye lavado y secado"
                value={formDescripcion}
                onChange={e => setFormDescripcion(e.target.value)}
                className="min-h-[80px]"
              />
            </div>

            <div className="flex items-center justify-between pt-4 border-t">
              <div>
                <Label>Servicio activo</Label>
                <p className="text-xs text-muted-foreground">Visible en la web de reservas</p>
              </div>
              <Switch
                checked={formActivo}
                onCheckedChange={setFormActivo}
                data-testid="modal-switch-activo"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} data-testid="modal-btn-guardar">
              {editingServicio ? "Guardar cambios" : "Crear servicio"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
